import pointAtPath from 'point-at-length'
import { clamp } from '@yuntu/shared'
import type { EdgeData, NodeData } from '../types'
import type { AbstractNode } from '../types'

export * from './gpu'

/**
 * 校验边数据
 *
 * @param nodes
 */
export const isValidEdge =
  (nodes: NodeData[]) =>
  (edge: EdgeData): boolean =>
    nodes.some((node) => node.id === edge.source) &&
    nodes.some((node) => node.id === edge.target)

/**
 * 获取文本的位置点
 *
 * @param {string} pathD 路径
 */
export function getTextPoint(pathD: string) {
  const points = pointAtPath(pathD)
  const textPoint = points.at(points.length() / 2)

  return {
    x: textPoint[0],
    y: textPoint[1],
  }
}

/**
 * 获取文本的旋转角度
 *
 * @returns
 */
export const getRotate = (
  source: {
    x: number
    y: number
  },
  target: {
    x: number
    y: number
  }
) => {
  const px = source.x
  const py = source.y
  const mx = target.x
  const my = target.y
  const x = Math.abs(px - mx)
  const y = Math.abs(py - my)
  const z = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2)) || 1
  const cos = y / z
  const radina = Math.acos(cos) //用反三角函数求弧度
  let angle = Math.floor(180 / (Math.PI / radina)) //将弧度转换成角度

  if (mx > px && my > py) {
    //鼠标在第四象限
    angle = 180 - angle
  }

  if (mx === px && my > py) {
    //鼠标在y轴负方向上
    angle = 180
  }

  if (mx > px && my === py) {
    //鼠标在x轴正方向上
    angle = 90
  }

  if (mx < px && my > py) {
    //鼠标在第三象限
    angle = 180 + angle
  }

  if (mx < px && my === py) {
    //鼠标在x轴负方向
    angle = 270
  }

  if (mx < px && my < py) {
    //鼠标在第二象限
    angle = 360 - angle
  }
  angle = angle - 90
  if (angle < 270 && angle > 90) {
    // 让文本正向显示
    angle = angle - 180
  }

  return angle
}

/**
 * 获取文本偏移量
 *
 * @param {Object} textPoint 字符定位
 * @param {Number} textRotate 边角度
 * @param {Number} offset 偏移量
 */
export function getTextTransform(
  textPoint: {
    x: number
    y: number
  },
  textRotate: number,
  offset = -8
) {
  const angle = (textRotate / 360) * Math.PI

  textPoint.x -= offset * Math.sin(angle)
  textPoint.y += offset * Math.cos(angle)

  return `rotate(${textRotate} ${textPoint.x},${textPoint.y})`
}

interface Rect {
  x: number
  x2: number
  y: number
  y2: number
}

interface Box {
  x: number
  y: number
  width: number
  height: number
}

/**
 * 计算包围盒
 *
 * @param box1
 * @param box2
 */
export const calcBoundsOfBoxes = (box1: Rect, box2: Rect) => ({
  x: Math.min(box1.x, box2.x),
  y: Math.min(box1.y, box2.y),
  x2: Math.max(box1.x2, box2.x2),
  y2: Math.max(box1.y2, box2.y2),
})

/**
 * 转换包围盒到矩形
 *
 * @param x
 * @param y
 * @param width
 * @param height
 */
export const rectToBox = ({ x, y, width, height }: Box): Rect => ({
  x,
  y,
  x2: x + width,
  y2: y + height,
})

/**
 * 转换矩形到包围盒
 *
 * @param x
 * @param y
 * @param x2
 * @param y2
 */
export const boxToRect = ({ x, y, x2, y2 }: Rect): Box => ({
  x,
  y,
  width: x2 - x,
  height: y2 - y,
})

/**
 * 计算节点围成的矩形
 *
 * @param nodes
 */
export const calcRectOfNodes = (nodes: AbstractNode[]) => {
  const box = nodes.reduce(
    (currBox, node) => {
      const { width, height } = node.element?.getBoundingClientRect() ?? {
        width: node.size[0],
        height: node.size[1],
      }

      // TODO: 解决圆形包围计算问题
      return calcBoundsOfBoxes(
        currBox,
        rectToBox({
          x: node.x,
          y: node.y,
          width,
          height,
        })
      )
    },
    { x: Infinity, y: Infinity, x2: -Infinity, y2: -Infinity }
  )

  return boxToRect(box)
}

/**
 * 根据包围盒计算偏移量
 *
 * @param box
 * @param width
 * @param height
 * @param minZoom
 * @param maxZoom
 * @param padding
 */
export const calcTransformForBounds = (
  box: Box,
  width: number,
  height: number,
  minZoom: number,
  maxZoom: number,
  padding: [number, number]
) => {
  const xZoom = width / (box.width * (1 + padding[0]))
  const yZoom = height / (box.height * (1 + padding[1]))
  const zoom = Math.min(xZoom, yZoom)
  const clampedZoom = clamp(zoom, minZoom, maxZoom)
  const boundsCenterX = box.x + box.width / 2
  const boundsCenterY = box.y + box.height / 2
  const x = width / 2 - boundsCenterX * clampedZoom
  const y = height / 2 - boundsCenterY * clampedZoom

  return [x, y, clampedZoom]
}
